import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Alert, Input as AntInput, Button, Modal, Spin } from 'antd';
import styles from '../chat.module.scss';
import useConversation from '../hooks/useConversation';
import MessageBubble from './MessageBubble';
import ConnectionIndicator from './ConnectionIndicator';
import { formatNameLastFirst } from '../../../shared/utils/formatName';

const NEAR_BOTTOM_PX = 80;
const LOAD_OLDER_PX = 60;

// Ikonica papirnog aviona za dugme "Pošalji".
const SendIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4 20-7z" />
  </svg>
);

const ChatModal = ({ open, conversation, onClose, afterClose }) => {
  const conversationId = conversation?.id || null;

  const {
    messages,
    historyLoading,
    olderLoading,
    hasMoreOlder,
    errorMessage,
    loadOlder,
    sendMessage,
    retryMessage,
  } = useConversation({ conversationId, open });

  const [text, setText] = useState('');
  const [showNewMessages, setShowNewMessages] = useState(false);

  const scrollRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const prevCountRef = useRef(0);
  const olderRestoreRef = useRef(null); // scrollHeight pre ucitavanja starijih

  const scrollToBottom = useCallback(() => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
    setShowNewMessages(false);
  }, []);

  // Reset scroll stanja kada se otvori/promeni razgovor.
  useEffect(() => {
    if (!open) return;
    setText('');
    setShowNewMessages(false);
    isNearBottomRef.current = true;
    prevCountRef.current = 0;
    olderRestoreRef.current = null;
  }, [open, conversationId]);

  // Posle svake promene liste poruka: ili vrati poziciju (posle "starijih"),
  // ili auto-skroluj na dno (ako je korisnik pri dnu), ili ponudi "Nove poruke".
  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    if (olderRestoreRef.current != null) {
      // Sacuvaj vizuelnu poziciju kada se starije poruke ubace na vrh.
      node.scrollTop = node.scrollHeight - olderRestoreRef.current;
      olderRestoreRef.current = null;
      prevCountRef.current = messages.length;
      return;
    }

    const grew = messages.length > prevCountRef.current;
    prevCountRef.current = messages.length;

    if (isNearBottomRef.current) {
      scrollToBottom();
    } else if (grew) {
      setShowNewMessages(true);
    }
  }, [messages, scrollToBottom]);

  const handleScroll = useCallback(() => {
    const node = scrollRef.current;
    if (!node) return;

    const distanceFromBottom =
      node.scrollHeight - node.scrollTop - node.clientHeight;
    isNearBottomRef.current = distanceFromBottom < NEAR_BOTTOM_PX;
    if (isNearBottomRef.current) setShowNewMessages(false);

    if (
      node.scrollTop < LOAD_OLDER_PX &&
      hasMoreOlder &&
      !olderLoading &&
      olderRestoreRef.current == null
    ) {
      olderRestoreRef.current = node.scrollHeight;
      loadOlder();
    }
  }, [hasMoreOlder, olderLoading, loadOlder]);

  const handleSend = useCallback(() => {
    const value = text.trim();
    if (!value) return; // prazan unos je no-op
    sendMessage(value);
    setText('');
    // Sopstvena poruka ide na dno -> osiguraj auto-scroll.
    isNearBottomRef.current = true;
  }, [text, sendMessage]);

  const handleKeyDown = useCallback(
    (event) => {
      // Enter salje; Shift+Enter je novi red.
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <Modal
      className="chat_modal"
      open={open}
      onCancel={onClose}
      afterClose={afterClose}
      footer={null}
      title={null}
      width={560}
      destroyOnHidden
      mask={{ closable: false }}
      keyboard={false}
    >
      <div className={styles.window}>
        <div className={styles.window__header}>
          <div className={styles.window__patient}>
            <span className={styles.window__name}>
              {formatNameLastFirst(conversation, '') || 'Razgovor'}
            </span>
          </div>
          <ConnectionIndicator />
        </div>

        <div
          className={styles.messages}
          ref={scrollRef}
          onScroll={handleScroll}
        >
          {hasMoreOlder && (
            <div className={styles.messages__top}>
              {olderLoading && <Spin size="small" />}
            </div>
          )}

          {historyLoading && messages.length === 0 ? (
            <div className={styles.messages__empty}>
              <Spin size="small" />
            </div>
          ) : errorMessage && messages.length === 0 ? (
            <Alert title={errorMessage} type="error" showIcon />
          ) : messages.length === 0 ? (
            <div className={styles.messages__empty}>
              Još uvek nema poruka u ovom razgovoru.
            </div>
          ) : (
            messages.map((message) => (
              // Key po clientMessageId (stabilan i za optimisticki i za potvrdjeni
              // balon) - inace bi ack promenio key i remontirao balon => treperenje.
              <MessageBubble
                key={message.clientMessageId || message.id}
                message={message}
                onRetry={retryMessage}
              />
            ))
          )}

          {showNewMessages && (
            <button
              type="button"
              className={styles.newMessages}
              onClick={scrollToBottom}
            >
              Nove poruke ↓
            </button>
          )}
        </div>

        <div className={styles.inputBar}>
          <div className={styles.inputBar__field}>
            <AntInput.TextArea
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Napišite poruku…"
              autoSize={{ minRows: 1, maxRows: 4 }}
            />
          </div>
          <Button
            type="primary"
            className={styles.sendButton}
            onClick={handleSend}
            disabled={!text.trim()}
            icon={<SendIcon />}
          >
            Pošalji
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ChatModal.propTypes = {
  open: PropTypes.bool.isRequired,
  conversation: PropTypes.shape({
    id: PropTypes.string,
    patientName: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  afterClose: PropTypes.func,
};

export default ChatModal;
